import { Button } from '@runtime/components/ui/Button'
import { FormField } from '@runtime/components/ui/Form'
import { Text } from '@runtime/components/ui/Text'
import type { I18nT } from '@runtime/core/i18n/i18n'
import FieldGroup from '@runtime/features/profile/auth/fields/FieldGroup'
import PasswordField from '@runtime/features/profile/auth/fields/PasswordField'
import styles from '@runtime/features/profile/auth/tabs/AuthTab.module.scss'

type ResetStage = 'request' | 'confirm' | 'done'

type AuthTabProps = {
  t: I18nT
  showReset: boolean
  authError: string
  authBusy: boolean
  loginEmail: string
  loginPassword: string
  showLoginPassword: boolean
  onLoginEmailChange: (value: string) => void
  onLoginPasswordChange: (value: string) => void
  onToggleLoginPassword: () => void
  onLogin: () => void
  onStartReset: () => void
  resetStage: ResetStage
  resetEmail: string
  resetToken: string
  resetPassword: string
  showResetPassword: boolean
  resetNotice: string
  resetError: string
  resetBusy: boolean
  onResetEmailChange: (value: string) => void
  onResetTokenChange: (value: string) => void
  onResetPasswordChange: (value: string) => void
  onToggleResetPassword: () => void
  onResetRequest: () => void
  onResetConfirm: () => void
  onExitReset: () => void
}

export default function AuthTab({
  t,
  showReset,
  authError,
  authBusy,
  loginEmail,
  loginPassword,
  showLoginPassword,
  onLoginEmailChange,
  onLoginPasswordChange,
  onToggleLoginPassword,
  onLogin,
  onStartReset,
  resetStage,
  resetEmail,
  resetToken,
  resetPassword,
  showResetPassword,
  resetNotice,
  resetError,
  resetBusy,
  onResetEmailChange,
  onResetTokenChange,
  onResetPasswordChange,
  onToggleResetPassword,
  onResetRequest,
  onResetConfirm,
  onExitReset,
}: AuthTabProps) {
  return (
    <div className={styles.loginForm} data-testid="login-form">
      {!showReset ? (
        <>
          <div className={styles.loginSectionTitle}>
            {t('profile.auth.loginTitle')}
          </div>
          {authError && (
            <Text variant="legend" className={styles.noticeError}>
              {authError}
            </Text>
          )}
          <FieldGroup label={t('profile.identity.emailLabel')}>
            <FormField
              round
              value={loginEmail}
              onChange={(event) => onLoginEmailChange(event.target.value)}
              placeholder={t('profile.identity.emailPlaceholder')}
              disabled={authBusy}
            />
          </FieldGroup>
          <FieldGroup label={t('profile.identity.passwordLabel')}>
            <PasswordField
              value={loginPassword}
              visible={showLoginPassword}
              placeholder={t('profile.identity.passwordPlaceholder')}
              disabled={authBusy}
              showLabel={t('profile.auth.showPassword')}
              hideLabel={t('profile.auth.hidePassword')}
              onChange={onLoginPasswordChange}
              onToggleVisibility={onToggleLoginPassword}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onLogin()
              }}
            />
          </FieldGroup>
          <div
            className={`${styles.loginFormFooter} ${styles.loginFormFooterSplit}`}
            data-testid="login-form-footer"
          >
            <button
              type="button"
              className={styles.loginTextBtn}
              onClick={onStartReset}
              disabled={authBusy}
            >
              {t('profile.auth.forgotLink')}
            </button>
            <Button type="button" onClick={onLogin} disabled={authBusy}>
              {authBusy
                ? t('profile.auth.loading')
                : t('profile.auth.loginAction')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.loginSectionTitle}>
            {t('profile.auth.resetTitle')}
          </div>
          <Text variant="legend" className={styles.notice}>
            {t('profile.auth.resetHelp')}
          </Text>
          {resetError && (
            <Text variant="legend" className={styles.noticeError}>
              {resetError}
            </Text>
          )}
          {resetNotice && (
            <Text variant="legend" className={styles.noticeSuccess}>
              {resetNotice}
            </Text>
          )}
          <FieldGroup label={t('profile.identity.emailLabel')}>
            <FormField
              round
              value={resetEmail}
              onChange={(event) => onResetEmailChange(event.target.value)}
              placeholder={t('profile.identity.emailPlaceholder')}
              disabled={resetBusy}
            />
          </FieldGroup>
          {resetStage === 'confirm' && (
            <>
              <FieldGroup label={t('profile.auth.resetCodeLabel')}>
                <FormField
                  round
                  value={resetToken}
                  onChange={(event) => onResetTokenChange(event.target.value)}
                  placeholder="123456"
                  disabled={resetBusy}
                />
              </FieldGroup>
              <FieldGroup label={t('profile.auth.resetPasswordLabel')}>
                <PasswordField
                  value={resetPassword}
                  visible={showResetPassword}
                  placeholder={t('profile.identity.passwordPlaceholder')}
                  disabled={resetBusy}
                  showLabel={t('profile.auth.showPassword')}
                  hideLabel={t('profile.auth.hidePassword')}
                  onChange={onResetPasswordChange}
                  onToggleVisibility={onToggleResetPassword}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && resetStage === 'confirm')
                      onResetConfirm()
                  }}
                />
              </FieldGroup>
            </>
          )}
          <div
            className={`${styles.loginFormFooter} ${styles.loginFormFooterSplit}`}
            data-testid="login-form-footer"
          >
            <button
              type="button"
              className={styles.loginTextBtn}
              onClick={onExitReset}
              disabled={resetBusy}
            >
              {t('profile.auth.resetBack')}
            </button>
            {resetStage === 'request' ? (
              <Button
                type="button"
                onClick={onResetRequest}
                disabled={resetBusy}
              >
                {resetBusy
                  ? t('profile.auth.loading')
                  : t('profile.auth.resetSendAction')}
              </Button>
            ) : resetStage === 'done' ? (
              <Button type="button" onClick={onExitReset} disabled={resetBusy}>
                {t('profile.auth.loginAction')}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={onResetConfirm}
                disabled={resetBusy}
              >
                {resetBusy
                  ? t('profile.auth.loading')
                  : t('profile.auth.resetAction')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
